import clsx from "clsx";
import { memo, useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Divider } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";

import { FILTER } from "../../consts/strings";
import {
  SECTION,
  PLANNED_READY_TIME,
  ROBOT_SUPPLIER,
  PICKUP_TYPE,
} from "../../consts/filters/";
import {
  driverManagementFilters,
  planManagementFilters,
} from "../../consts/filters/filtersDefinition";
import { palette } from "../../theme/palette";

import { getSelectedDepot } from "../../reducers/ownApp/selectors";
import {
  getMatrixProfileType,
  isEditOneModeSelector,
  isProfileSelectedSelector,
  pickupPartnersSelector,
  robotSuppliersSelector,
  sectionsSelector,
  selectedOverlaySelector,
  wavesSelector,
} from "../../reducers/matrixProfile/selectors";
import { driverEditModeSelector } from "../../reducers/driverManagement/selectors";
import {
  setFilter,
  resetVolumeOverlayFilters,
  resetFilter,
} from "../../reducers/filters";
import { appliedFiltersCountSelector } from "../../reducers/filters/selectors";

import { useLocationDetails } from "../../hooks/useLocationDetails";

import { Text, View } from "../Base";
import { CloseIcon, MenuIcon } from "../Icons";
import FilterSection from "./FilterSection";
import FilterAutocomplete from "./FilterAutocomplete";

const useStyles = makeStyles(({ typography }) => ({
  container: {
    width: "16.5rem",
    background: palette.slate,
    top: typography.pxToRem(100),
    transform: "translateX(16.5rem)",
    right: 0,
    bottom: 0,
    display: "flex",
    position: "absolute",
    flexDirection: "column",
    transition: "transform .2s",
    zIndex: 100,
  },
  containerOpened: {
    transform: "translateX(0)",
  },
  header: {
    fontSize: "1.125rem",
    lineHeight: "1.25rem",
    marginTop: "0.938rem",
    color: palette.typography.white.solid,
    paddingLeft: "1.25rem",
    marginBottom: typography.pxToRem(4),
  },
  divider: {
    height: "0.063rem",
    background: palette.typography.white.ghost,
  },
  list: {
    paddingLeft: "1.25rem",
    overflowY: "auto",
  },
  menuButton: {
    width: "2.125rem",
    height: "2.5rem",
    position: "absolute",
    left: "-2.125rem",
    background: palette.slate,
    top: "calc(50% - 1.25rem)",
    color: palette.typography.white.solid,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderTopLeftRadius: "0.313rem",
    borderBottomLeftRadius: "0.313rem",
    boxShadow: "0 0.25rem 0.25rem rgba(0, 0, 0, 0.25)",
    cursor: "pointer",

    "& svg": {
      height: "1rem",
    },
  },
  counter: {
    width: "1rem",
    height: "1rem",
    position: "absolute",
    left: "-2.5rem",
    top: "calc(50% - 1.5rem)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "0.5rem",
    background: palette.dpdRed,
    color: palette.typography.white.solid,
    fontSize: "0.7rem",
    fontWeight: 300,
  },
  disabled: {
    cursor: "not-allowed",
    color: palette.typography.white.disabled,
  },
}));

const FilterPanel = memo(() => {
  const selectedDepot = useSelector(getSelectedDepot);
  const filtersCount = useSelector(appliedFiltersCountSelector);
  const profileSelected = useSelector(isProfileSelectedSelector);
  const planType = useSelector(getMatrixProfileType);
  const isEditOneMode = useSelector(isEditOneModeSelector);
  const driverEditMode = useSelector(driverEditModeSelector);
  const selectedOverlay = useSelector(selectedOverlaySelector);
  const sections = useSelector(sectionsSelector);
  const pickupPartners = useSelector(pickupPartnersSelector);
  const robotSuppliers = useSelector(robotSuppliersSelector);
  const waves = useSelector(wavesSelector);
  const styles = useStyles();
  const dispatch = useDispatch();
  const { application, isPlanManagement, isDriverManagement } =
    useLocationDetails();
  const [open, setOpen] = useState(false);

  let filters = null;
  if (selectedDepot && isDriverManagement) filters = driverManagementFilters;
  else if (profileSelected && isPlanManagement) filters = planManagementFilters;

  useEffect(() => {
    dispatch(resetFilter());
  }, [dispatch, application]);

  const onChange = useCallback(
    (event) => {
      dispatch(setFilter(event));
    },
    [dispatch]
  );

  const handleOnClickFilterButton = useCallback(() => {
    if (isEditOneMode) return;
    setOpen(!open);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditOneMode, open]);

  useEffect(() => {
    (isEditOneMode || driverEditMode) && setOpen(false);
  }, [isEditOneMode, driverEditMode]);

  useEffect(() => {
    !selectedOverlay && dispatch(resetVolumeOverlayFilters());
  }, [dispatch, selectedOverlay]);

  if (!filters) return null;
  else
    return (
      <View className={clsx(styles.container, open && styles.containerOpened)}>
        <View
          className={clsx(
            styles.menuButton,
            open && styles.closed,
            isEditOneMode && styles.disabled
          )}
          onClick={handleOnClickFilterButton}
          data-testid="open-close-route-list-filter"
        >
          {open ? <CloseIcon /> : <MenuIcon />}
        </View>
        {!open && !isEditOneMode && !!filtersCount && (
          <View className={styles.counter}>{filtersCount}</View>
        )}
        <Text className={styles.header}>{FILTER}</Text>
        <Divider className={styles.divider} />
        <View className={styles.list}>
          {filters.map((filter, id) => {
            const disabled =
              (filter.volumeOverlayRequired && !selectedOverlay) ||
              (filter.planTypeRequired && filter.planTypeRequired !== planType);
            if (filter.field === ROBOT_SUPPLIER)
              return (
                <FilterAutocomplete
                  key={`${filter.title}_${id}`}
                  disabled={disabled}
                  filter={{
                    ...filter,
                    values: filter.mapToValues(robotSuppliers),
                  }}
                  onChange={onChange}
                />
              );
            else if (filter.field === SECTION)
              return (
                <FilterAutocomplete
                  key={`${filter.title}_${id}`}
                  disabled={disabled}
                  filter={{
                    ...filter,
                    values: filter.mapToValues(sections),
                  }}
                  onChange={onChange}
                />
              );
            else if (filter.field === PICKUP_TYPE)
              return (
                <FilterSection
                  key={`${filter.title}_${id}`}
                  disabled={disabled}
                  filter={{
                    ...filter,
                    values: filter.mapToValues(pickupPartners),
                  }}
                  onChange={onChange}
                />
              );
            else if (filter.field === PLANNED_READY_TIME)
              return (
                <FilterSection
                  key={`${filter.title}_${id}`}
                  disabled={disabled}
                  filter={{
                    ...filter,
                    values: filter.mapToValues(waves),
                  }}
                  onChange={onChange}
                />
              );
            else
              return (
                <FilterSection
                  key={`${filter.title}_${id}`}
                  filter={filter}
                  disabled={disabled}
                  onChange={onChange}
                />
              );
          })}
        </View>
      </View>
    );
});

export default FilterPanel;
